export interface PageMeta {
  title: string;
  description?: string;
  keywords?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonical?: string;
}

export interface MetaConfig {
  default: PageMeta;
  pages: Record<string, PageMeta>;
  patterns?: Array<{
    pattern: RegExp | string;
    meta: PageMeta;
  }>;
}

export const META_CONFIG: MetaConfig = {
  default: {
    title: 'Syncrony Data Uploader',
    description:
      'Professional data upload and management platform for clinical research',
    keywords: [
      'data upload',
      'clinical research',
      'file management',
      'syncrony',
    ],
    ogTitle: 'Syncrony Data Uploader',
    ogDescription:
      'Professional data upload and management platform for clinical research',
    ogImage: '/favicon.svg',
  },
  pages: {
    // Auth pages (keeping for backward compatibility)
    '/auth/login': {
      title: 'Sign In - Syncrony Data Uploader',
      description:
        'Sign in to your Syncrony Data Uploader account to manage your clinical research data',
      keywords: ['login', 'sign in', 'authentication', 'syncrony'],
    },
    '/auth/register': {
      title: 'Register - Syncrony Data Uploader',
      description:
        'Create a new account to start using Syncrony Data Uploader for clinical research',
      keywords: ['register', 'sign up', 'account creation', 'syncrony'],
    },

    // Dashboard pages
    '/dashboard': {
      title: 'Dashboard - Syncrony Data Uploader',
      description:
        'Overview of your clinical research data and upload activities',
      keywords: ['dashboard', 'overview', 'analytics', 'syncrony'],
    },

    // File management pages
    '/file-upload': {
      title: 'File Upload - Syncrony Data Uploader',
      description: 'Upload and manage your clinical research files securely',
      keywords: ['file upload', 'data import', 'file management', 'syncrony'],
    },
    '/study-files': {
      title: 'Study Files - Syncrony Data Uploader',
      description: 'Browse and manage your study files and research data',
      keywords: ['study files', 'research data', 'file browser', 'syncrony'],
    },
    '/csv-upload': {
      title: 'CSV Upload - Syncrony Data Uploader',
      description: 'Upload CSV data files for clinical research analysis',
      keywords: ['csv upload', 'data import', 'spreadsheet', 'syncrony'],
    },
    '/data-import': {
      title: 'Data Import - Syncrony Data Uploader',
      description: 'Import and manage study data for clinical research',
      keywords: ['study data', 'data import', 'research', 'syncrony'],
    },
    '/data-import/:step': {
      title: 'Data Import - Syncrony Data Uploader',
      description: 'Import and manage study data for clinical research',
      keywords: ['study data', 'data import', 'research', 'syncrony'],
    },
    '/data-import/mapping/:mappingId': {
      title: 'Data Import Mapping - Syncrony Data Uploader',
      description: 'Configure data import mapping for study data',
      keywords: ['data import', 'mapping', 'configuration', 'syncrony'],
    },
    '/data-import/study_forms': {
      title: 'Study Forms Templates - Syncrony Data Uploader',
      description: 'Manage study forms templates for data import',
      keywords: ['study forms', 'templates', 'data import', 'syncrony'],
    },
    '/file-upload/files': {
      title: 'Uploaded Files - Syncrony Data Uploader',
      description: 'View and manage your uploaded files',
      keywords: ['uploaded files', 'file management', 'files', 'syncrony'],
    },
    '/login': {
      title: 'Sign In - Syncrony Data Uploader',
      description:
        'Sign in to your Syncrony Data Uploader account to manage your clinical research data',
      keywords: ['login', 'sign in', 'authentication', 'syncrony'],
    },
    '/design-system/status-chip': {
      title: 'Status Chip - Design System - Syncrony Data Uploader',
      description: 'Status chip component documentation',
      keywords: ['design system', 'components', 'status chip', 'syncrony'],
    },
    '/design-system/avatar': {
      title: 'Avatar - Design System - Syncrony Data Uploader',
      description: 'Avatar component documentation',
      keywords: ['design system', 'components', 'avatar', 'syncrony'],
    },
    '/design-system/toast': {
      title: 'Toast - Design System - Syncrony Data Uploader',
      description: 'Toast notification component documentation',
      keywords: ['design system', 'components', 'toast', 'syncrony'],
    },

    // Management pages
    '/whitelist-management': {
      title: 'Whitelist Management - Syncrony Data Uploader',
      description: 'Manage file whitelist settings and permissions',
      keywords: ['whitelist', 'permissions', 'file security', 'syncrony'],
    },
    '/audit-trail': {
      title: 'Audit Trail - Syncrony Data Uploader',
      description: 'View activity logs and audit trail for compliance',
      keywords: ['audit trail', 'activity logs', 'compliance', 'syncrony'],
    },

    // Settings pages
    '/settings': {
      title: 'Settings - Syncrony Data Uploader',
      description: 'Manage your account settings and preferences',
      keywords: ['settings', 'preferences', 'account', 'syncrony'],
    },

    // Error pages
    '/404': {
      title: 'Page Not Found - Syncrony Data Uploader',
      description: 'The page you are looking for could not be found',
      keywords: ['404', 'not found', 'error', 'syncrony'],
    },
    '/500': {
      title: 'Server Error - Syncrony Data Uploader',
      description: 'An internal server error occurred. Please try again later.',
      keywords: ['500', 'server error', 'error', 'syncrony'],
    },
    '/403': {
      title: 'Access Denied - Syncrony Data Uploader',
      description: 'You do not have permission to access this resource',
      keywords: ['403', 'access denied', 'forbidden', 'syncrony'],
    },
  },
};

/**
 * Normalizes a pathname by removing trailing slashes and ensuring consistent format
 */
function normalizePathname(pathname: string): string {
  return pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;
}

/**
 * Checks if a pathname matches a route pattern
 * Supports patterns like '/data-import/:step' or '/data-import/mapping/:mappingId'
 */
function matchesPattern(pathname: string, pattern: string): boolean {
  // Convert pattern to regex
  const regexPattern = pattern
    .replace(/:[^/]+/g, '[^/]+') // Replace :param with [^/]+
    .replace(/\//g, '\\/'); // Escape slashes
  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(pathname);
}

/**
 * Finds the best matching route for a pathname
 * Priority: exact match > pattern match > prefix match > default
 */
function findMatchingRoute(pathname: string): PageMeta | null {
  const normalizedPath = normalizePathname(pathname);

  // 1. Try exact match first
  if (META_CONFIG.pages[normalizedPath]) {
    return META_CONFIG.pages[normalizedPath];
  }

  // 2. Try pattern matching for dynamic routes
  for (const [route, meta] of Object.entries(META_CONFIG.pages)) {
    if (route.includes(':') && matchesPattern(normalizedPath, route)) {
      return meta;
    }
  }

  // 3. Try prefix matching for nested routes
  // Sort by length (longest first) to match more specific routes first
  // Only match if the path starts with the route followed by a slash (nested route)
  // or if it's an exact match (already checked above, but keeping for clarity)
  const sortedRoutes = Object.keys(META_CONFIG.pages)
    .filter(route => !route.includes(':')) // Exclude pattern routes, they're already checked
    .sort((a, b) => b.length - a.length);
  for (const route of sortedRoutes) {
    // Check if path is a nested route (starts with route + '/')
    // or if it's an exact match (shouldn't happen here, but safe check)
    if (normalizedPath.startsWith(route + '/')) {
      return META_CONFIG.pages[route];
    }
  }

  return null;
}

/**
 * Gets page meta information for a given pathname
 * Supports exact matches, pattern matches (for dynamic routes), and prefix matches (for nested routes)
 */
export function getPageMeta(pathname: string): PageMeta {
  const matchingMeta = findMatchingRoute(pathname);
  
  if (matchingMeta) {
    return {
      ...META_CONFIG.default,
      ...matchingMeta,
    };
  }
  
  return META_CONFIG.default;
}

export function updatePageMeta(meta: PageMeta): void {
  // Update document title
  document.title = meta.title;

  // Update meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute('content', meta.description || '');

  // Update meta keywords
  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }
  metaKeywords.setAttribute('content', meta.keywords?.join(', ') || '');

  // Update Open Graph tags
  let ogTitle = document.querySelector('meta[property="og:title"]');
  if (!ogTitle) {
    ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    document.head.appendChild(ogTitle);
  }
  ogTitle.setAttribute('content', meta.ogTitle || meta.title);

  let ogDescription = document.querySelector('meta[property="og:description"]');
  if (!ogDescription) {
    ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    document.head.appendChild(ogDescription);
  }
  ogDescription.setAttribute(
    'content',
    meta.ogDescription || meta.description || ''
  );

  let ogImage = document.querySelector('meta[property="og:image"]');
  if (!ogImage) {
    ogImage = document.createElement('meta');
    ogImage.setAttribute('property', 'og:image');
    document.head.appendChild(ogImage);
  }
  ogImage.setAttribute('content', meta.ogImage || '');

  // Update canonical URL
  if (meta.canonical) {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', meta.canonical);
  }
}
